import {
    useEffect,
    useState,
} from "react";
import {
    styled,
    Button,
    Tooltip,
} from "@mui/material";
import { keyframes } from "@mui/system";
import { getLineButtonStyles } from "../../../../utils/buttonStyle";
import SVG from "react-inlinesvg";
import { LOGIN_METHOD } from "../../../../constants";
import {
    LINE_PHONE_METHODS,
    LINE_SESSION_METHODS,
    WHATSAPP_METHODS,
    LINE_LIFF_METHODS,
} from "./loginMethods";
import { useTranslation } from "react-i18next";
import LineIcon from "../../../../assets/icons/Line.svg";
import WhatsappIcon from "../../../../assets/icons/whatsapp.svg";

const bounce = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
  100% { transform: translateY(0); }
`;

export const SubmitButton = styled(Button)({
    borderRadius: 10,
    width: "100%",
    backgroundColor: "#F5F8FF",
    color: "#141416",
    fontSize: 14,
    padding: "17px 20px",
    "&:disabled": {
        backgroundColor: "#E0E0E0", // Replace MUI's partially transparent disabled color for sticky positioning.
    },
    "&:hover": {
        backgroundColor: "#F5F8FF",
    },
    "& svg": {
        marginRight: 10,
        width: 20,
        height: 20
    }
});

interface SubmitButtonProp {
    appName: string,
    pageAttentionTooltipTitle: string,
    isEGC: boolean,
    disableButton: boolean,
    accepted: boolean
    handleSubmit: () => void,
    handleSsoSubmit: () => void,
    handleLiffSubmit: () => void,
}

export const AppSubmitButton = ({
    appName,
    pageAttentionTooltipTitle,
    isEGC,
    disableButton,
    accepted,
    handleSubmit,
    handleLiffSubmit,
    handleSsoSubmit
}: SubmitButtonProp) => {

    const { t, i18n } = useTranslation();

    const [open, setOpen] = useState(false);
    const [clicked, setClicked] = useState(false);

    useEffect(
        () => {
            if (clicked) {
                return;
            }

            // 5 seconds
            const timer = setTimeout(
                () => { setOpen(true); },
                5000
            );

            return () => {
                clearTimeout(timer);
            };
        },
        [clicked]
    );

    const renderSubmitButton = (
        lable: string,
        onClick: () => void,
        isLine: boolean,
        marginTop: number,
        disabled: boolean,
        icon?: string
    ) => (
        <Tooltip
            arrow
            disableHoverListener
            disableFocusListener
            disableTouchListener
            onClose={() => setOpen(false)}
            open={open}
            placement="top"
            slotProps={{
                popper: {
                    disablePortal: true,
                    slotProps: {
                        root: {
                            style: { zIndex: 0 }
                        }
                    }
                },
                tooltip: {
                    sx: {
                        fontSize: 14,
                        fontWeight: 700,
                        animation: `${bounce} 1s infinite`,
                        padding: "4px 8px",
                    }
                }
            }}
            title={pageAttentionTooltipTitle}
        >
            <SubmitButton
                style={
                    {
                        marginTop,
                        ...(
                            isLine
                                ? getLineButtonStyles(disabled)
                                : {}
                        ),
                    }
                }
                variant="contained"
                onClick={
                    () => {
                        setClicked(true);
                        onClick();
                    }
                }
                disabled={disabled}
            >
                {icon && <SVG src={icon} />}
                {lable}
            </SubmitButton>
        </Tooltip>
    );

    switch (true) {
        case LINE_PHONE_METHODS.includes(appName):
            return renderSubmitButton(t("receiveMessageViaLine"), handleSubmit, true, 20, disableButton, LineIcon);

        case LINE_SESSION_METHODS.includes(appName): {
            if (i18n.language === "ja" && !isEGC) {
                return renderSubmitButton(t("receiveMessageViaLineJPlogin"), handleSubmit, true, 20, !accepted);
            }
            else {
                return renderSubmitButton(t("receiveMessageViaLine"), handleSubmit, true, 20, !accepted, LineIcon);
            }
        }

        case appName === LOGIN_METHOD.LINE_SSO:
            return renderSubmitButton(t("receiveMessageViaLine"), handleSsoSubmit, true, 20, !accepted, LineIcon);

        case appName === LOGIN_METHOD.SMS:
            return renderSubmitButton(t("receiveMessageViaSMS"), handleSubmit, false, 18, disableButton);

        case WHATSAPP_METHODS.includes(appName):
            return renderSubmitButton(t("receiveMessageViaWhatsapp"), handleSubmit, false, 18, disableButton, WhatsappIcon);

        case LINE_LIFF_METHODS.includes(appName):
            return renderSubmitButton(t("receiveMessageViaLine"), handleLiffSubmit, true, 20, !accepted, LineIcon);

        default:
            return null;
    }
};
